import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDeviceReadings } from "@features/mx/device-management/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableColumn } from "@shared/types/table";
import { Card } from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import { BarChart3 } from "lucide-react";
import { ReadingsFilters } from "./readingFilter";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";

interface ReadingsFiltersType {
  fromDate: string;
  toDate: string;
  status: string;
}


export const ReadingsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { id } = useParams();
  const [searchReading, setSearchReading] = useState("");
  const [filters, setFilters] = useState<ReadingsFiltersType>({
    fromDate: "",
    toDate: "",
    status: "all",
  });

  const { data: readingsList, isLoading } = useDeviceReadings({
    meterId: id,
    page: currentPage,
    pageSize: PAGE_SIZE,
    searchData: searchReading,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    status: filters.status !== "all" ? filters.status : undefined,
  });

  const handleFiltersChange = (newFilters: ReadingsFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ fromDate: "", toDate: "", status: "all" });
    setSearchReading("");
    setCurrentPage(1);
  };

  // ✅ Use useMemo for stable columns
  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "updatedOn",
        header: "Date",
        type: "text",
        headerTooltip:
          "Timestamp when the reading was recorded. Used for billing cycle alignment and trend analysis.",
      },

      {
        key: "readType",
        header: "Read Type",
        type: "text",
        headerTooltip:
          "Method used to collect this reading: Manual (field entry), Photo (image-based), or Smart (automatic transmission).",
      },
      {
        key: "meterReading",
        header: "Reading",
        type: "custom",
        render: (value) => (
          <span className="font-medium text-gray-900">{value ?? "—"}</span>
        ),
        headerTooltip:
          "Cumulative meter value in cubic meters (m³). Consumption is calculated by subtracting previous reading from current reading.",
      },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getClassName: (value: string) => getStatusBadgeClass(value),
        },
        headerTooltip:
          "Validation status: Normal (passes all checks), Faulty (requires attention), RCNT (recent reading).",
      },
      {
        key: "meterReaderName",
        header: "Reader",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "N/A"}</p>
            <p className="text-xs text-muted-foreground">
              {row.readType }
            </p>
          </div>
        ),
        headerTooltip:
          "Field technician or system that collected this reading. Used for accountability and quality tracking.",
      },
      {
        key: "validatorName",
        header: "Validator",
        render: (value, row) => (
          <div>
            <p className="font-medium">{value ?? "N/A"}</p>
            <p className="text-xs text-muted-foreground">
              {row.validationDate ?? ""}
            </p>
          </div>
        ),
        headerTooltip:
          "System or supervisor that validated this reading against business rules and historical patterns.",
      },
    ],
    []
  );

  const pagination = readingsList
    ? {
        currentPage,
        totalPages: Math.ceil(readingsList.count / PAGE_SIZE),
        totalItems: readingsList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, readingsList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  return (
    <Card className="shadow-sm">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader
            title="Readings & Performance"
            subtitle="Detailed view of reading history and validation outcomes"
            icon={BarChart3}
          />
        </div>

        <GenericTable
          data={readingsList?.results ?? []}
          columns={columns}
          rowKey="id"
          pagination={pagination}
          onPageChange={(page) => setCurrentPage(page)}
          onSearch={(term) => {
            setSearchReading(term);
            setCurrentPage(1);
          }}
          searchPlaceholder="Search readings..."
          filterPanel={
            <ReadingsFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
            />
          }
          onExport={() => {}}
          emptyMessage="No readings found"
        />
      </div>
    </Card>
  );
};
